import {
  AIMessage as LangChainAIMessage,
  type ToolCall,
  type ToolCallChunk,
} from "@langchain/core/messages";
import { captureSentryException } from "src/utils/sentry";
import { useStore } from "../../../stores";
import { useSnackbar } from "../../Snackbar";
import { MarkdownText } from "../../ui/MarkdownText";
import type { ChatMessage, ChatToolCall, ChatToolMessage } from "../types";
import { CommandBar } from "./shared";
import { ThinkingBlock } from "./ThinkingBlock";
import { TOOL_CONFIGS } from "./utils/tool-calls";

interface AIMessageProps {
  message: ChatMessage;
  isLoading: boolean;
  isLastMessage: boolean;
  toolResultsMappedToToolCall?: ReadonlyMap<string, ChatToolMessage>;
}

type ToolBlock = ChatToolCall;

const getReasoningText = (block: any): string | null => {
  const text =
    typeof block.reasoning === "string"
      ? block.reasoning
      : typeof block.text === "string"
        ? block.text
        : typeof block.thinking === "string"
          ? block.thinking
          : null;

  if (text && text.trim().length > 0) {
    return text;
  }

  if (Array.isArray(block.summary)) {
    const summary = block.summary
      .map((item: any) =>
        typeof item === "string"
          ? item
          : typeof item?.text === "string"
            ? item.text
            : ""
      )
      .filter((item: string) => item.trim().length > 0)
      .join("\n");

    return summary.length > 0 ? summary : null;
  }

  return null;
};

const getToolCallBlocks = (message: any, contentBlocks: any[]): ToolBlock[] => {
  const contentToolBlocks = contentBlocks.filter(
    (block) =>
      block.type === "tool_call" ||
      block.type === "tool_call_chunk" ||
      block.type === "invalid_tool_call"
  ) as ToolBlock[];

  const rawToolCalls = Array.isArray(message.tool_calls)
    ? (message.tool_calls as Array<ToolCall | ToolCallChunk>)
    : [];

  if (rawToolCalls.length === 0) {
    return contentToolBlocks;
  }

  const rawToolCallsById = new Map<string, ToolBlock>();
  rawToolCalls.forEach((toolCall) => {
    if (typeof toolCall?.id === "string" && toolCall.id.length > 0) {
      rawToolCallsById.set(toolCall.id, toolCall);
    }
  });

  const normalizedContentBlocks = contentToolBlocks.map((block) => {
    const blockId = typeof block.id === "string" ? block.id : undefined;
    if (!blockId) {
      return block;
    }

    const rawToolCall = rawToolCallsById.get(blockId);
    if (!rawToolCall) {
      return block;
    }

    return {
      ...block,
      type: "tool_call" as const,
      id: rawToolCall.id ?? block.id,
      name: rawToolCall.name ?? block.name,
      args: rawToolCall.args ?? block.args,
    };
  });
  const dedupedContentBlocks: ToolBlock[] = [];
  const seenContentBlockIds = new Set<string>();
  normalizedContentBlocks.forEach((block) => {
    const blockId = "id" in block ? block.id : undefined;
    if (typeof blockId === "string" && blockId.length > 0) {
      if (seenContentBlockIds.has(blockId)) {
        return;
      }
      seenContentBlockIds.add(blockId);
    }

    dedupedContentBlocks.push(block);
  });

  const seenToolCallIds = new Set(
    dedupedContentBlocks
      .map((block) => block.id)
      .filter((id): id is string => typeof id === "string" && id.length > 0)
  );

  const missingToolCalls = rawToolCalls
    .filter((toolCall) => {
      if (typeof toolCall?.id !== "string" || toolCall.id.length === 0) {
        return true;
      }
      return !seenToolCallIds.has(toolCall.id);
    })
    .map((toolCall) => ({
      type: "tool_call" as const,
      id: toolCall.id,
      name: toolCall.name,
      args: toolCall.args,
    }));

  return [...dedupedContentBlocks, ...missingToolCalls];
};

export const getAIMessageRenderState = (
  message: ChatMessage,
  toolResultsMappedToToolCall?: ReadonlyMap<string, ChatToolMessage>
) => {
  const standardMessage = new LangChainAIMessage({
    content: message.content,
    id: message.id,
    tool_calls: message.tool_calls as any,
  });
  const thinkingBlocks = standardMessage.contentBlocks.filter(
    (block) => block.type === "reasoning"
  );
  const anthropicThinkingBlocks = Array.isArray(message.content)
    ? message.content.filter(
        (block) =>
          typeof block === "object" &&
          block !== null &&
          Reflect.get(block, "type") === "thinking"
      )
    : [];
  const thinkingMessages = [...thinkingBlocks, ...anthropicThinkingBlocks]
    .map(getReasoningText)
    .filter((message): message is string => message !== null);
  const textBlocks = standardMessage.contentBlocks.filter(
    (block) => block.type === "text"
  );
  const toolCallBlocks = getToolCallBlocks(
    message,
    standardMessage.contentBlocks
  );
  const textContent = textBlocks.map((block) => block.text).join("");

  const hasToolCalls = toolCallBlocks.length > 0;
  const hasThinkingMessages = thinkingMessages.length > 0;
  const everyToolCallHidden = toolCallBlocks.every((tc) => {
    const name = tc.name;
    return name ? TOOL_CONFIGS[name]?.hidden : false;
  });
  const hasPendingVisibleToolCall = toolCallBlocks.some((toolCall) => {
    const name = toolCall.name;
    if (name && TOOL_CONFIGS[name]?.hidden) {
      return false;
    }

    const toolCallId = "id" in toolCall ? (toolCall.id as string) : undefined;
    return !toolCallId || !toolResultsMappedToToolCall?.get(toolCallId);
  });

  return {
    thinkingMessages,
    textContent,
    toolCallBlocks,
    hasToolCalls,
    hasThinkingMessages,
    everyToolCallHidden,
    hasPendingVisibleToolCall,
  };
};

export function AIMessage({
  message,
  isLoading,
  isLastMessage,
  toolResultsMappedToToolCall,
}: AIMessageProps) {
  const { thinkingMessages, textContent, hasToolCalls, hasThinkingMessages } =
    getAIMessageRenderState(message, toolResultsMappedToToolCall);
  const { agentStore } = useStore();
  const { showSnackbar } = useSnackbar();

  // Handle feedback submission
  const handleFeedback = async (
    type: "positive" | "negative",
    comment?: string
  ) => {
    if (!agentStore.currentThreadId) {
      captureSentryException(new Error("No current thread ID for feedback"));
      return;
    }

    // Get current thread metadata
    const currentThread = await agentStore.client.threads.get(
      agentStore.currentThreadId
    );

    // Prepare feedback object
    const feedback = {
      type,
      comment: comment || "",
      timestamp: Date.now(),
      messageId: message?.id,
    };

    // Update thread metadata with feedback
    await agentStore.client.threads.update(agentStore.currentThreadId, {
      metadata: {
        ...currentThread?.metadata,
        feedback: [
          ...(Array.isArray(currentThread?.metadata?.feedback)
            ? currentThread.metadata.feedback
            : []),
          feedback,
        ],
      },
    });

    showSnackbar("Thank you for your feedback.");
  };

  // Reasoning can arrive before assistant text/tool chunks; keep that message
  // mounted so users see the trace as soon as the provider streams it.
  if (textContent.length === 0 && !hasToolCalls && !hasThinkingMessages) {
    return null;
  }

  return (
    <div className="group flex items-start gap-2  p-0">
      <div className={`flex flex-1 flex-col gap-2 max-w-full`}>
        {/* Render thinking blocks if present */}
        {thinkingMessages.length > 0 && (
          <ThinkingBlock
            thinkingMessages={thinkingMessages}
            defaultExpanded={isLoading && isLastMessage}
          />
        )}

        {/* Render text content */}
        {textContent.length > 0 && (
          <div>
            <MarkdownText collapseReferences>{textContent}</MarkdownText>
          </div>
        )}

        {textContent.length > 0 && (
          <div>
            <CommandBar
              content={textContent}
              isLoading={isLoading}
              isAiMessage={true}
              isLastMessage={isLastMessage}
              onFeedback={handleFeedback}
            />
          </div>
        )}
      </div>
    </div>
  );
}
